import { Action, Dispatch } from 'redux';
import { OptionsViewModel } from '@models/common.models';
import { authService } from '@services/AuthService';
import { customerService } from '@services/CustomerService';
import { AppState } from '../../..';
import { utilityService } from '@services/UtilityService';
import { realEstateService } from '@services/RealEstateService';
import { escrowService } from '@services/EscrowService';
import { transactionCoordinatorService } from '@services/TransactionCoordinatorService';
import { contractService } from '@services/ContractService';
import { DynamicSelectIdentifier } from '@common/components';
import { propertyManagementService } from '@services/PropertyManagementService';
import { documentService } from '@services/DocumentService';
import { vendorService } from '@services/VendorService';
import { swoService } from '@services/ServiceWorkOrderService';
import { authorizationService } from '@services/AuthorizationService';
import { invoiceService } from '@services/InvoiceService';
import { swoPurchasingService } from '@services/SwoPurchasingService';
import { cviService } from '@services/CVIService';
import { realEstateAgentsService } from '@services/RealEstateAgentsService';
import { realEstateOfficesService } from '@services/RealEstateOfficesService';
import { documentHistoryService } from '@services/DocumentHistoryService';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum DynamicSelectActionType {
  LoadOptionsInit = 'DynamicSelectLoadOptionsInit',
  LoadOptionsFail = 'DynamicSelectLoadOptionsFail',
  LoadOptionsSuccess = 'DynamicSelectLoadOptionsSuccess',
  ClearOptions = 'DynamicSelectClearOptions'
}

/*
    This is necessary to provide an instance of the service to be used -
    if you try to provide the function directly to the Getters map, javascript will try to call it as a 'static' function,
    and will not have the necessary information to make the request, as 'this' is undefined
*/
const getCompanies = () => authService.getCompanies();
const getCustomerTypes = () => customerService.getCustomerTypes();
const getPhoneTypes = () => utilityService.getPhoneTypes();
const getContractCommentTypes = () => contractService.getContractCommentTypes();
const getCustomerStatuses = () => customerService.getCustomerStatuses();
const getCustomerLanguages = () => customerService.getLanguages();
const getDocumentStatuses = () => documentHistoryService.getDocumentStatuses();
const getRealEstateAgentStatuses = () => realEstateAgentsService.getStatuses();
const getRealEstateAgentRatings = () => realEstateAgentsService.getRatings();
const getRealEstateAgentTypes = () => realEstateAgentsService.getTypes();
const getEscrowOfficeGroups = () => escrowService.getOfficeGroups();
const getEscrowOfficeStatuses = () => escrowService.getOfficeStatuses();
const getEscrowOfficerTypes = () => escrowService.getOfficerTypes();
const getEscrowOfficerStatuses = () => escrowService.getOfficerStatuses();
const getRealEstateOfficeGroups = () => realEstateOfficesService.getGroups();
const getRealEstateOfficeStatuses = () => realEstateOfficesService.getStatuses();
const getRealEstateOfficeEOCarriers = () => realEstateService.getEOCarriers();
const getTransactionCoordinatorTypes = () => transactionCoordinatorService.getTransactionCoordinatorTypes();
const getTransactionCoordinatorStatuses = () => transactionCoordinatorService.getTransactionCoordinatorStatuses();
const getUSStates = () => utilityService.getStates();
const getUSFacilityStates = () => swoPurchasingService.getFacilityStates();
const getPropertyManagmentOfficeStatuses = () => propertyManagementService.getStatuses();
const getPropertyManagerStatuses = () => propertyManagementService.getPropertyManagerStatuses();
const getPropertyManagerTypes = () => propertyManagementService.getPropertyManagerTypes();
const getEdocTypes = () => documentService.getEdocTypes();
const getVendorDocumentTypes = () => vendorService.getDocumentTypes();
const getVendorGroups = () => vendorService.getVendorGroups();
const getVendorTypes = () => vendorService.getVendorTypes();
const getVendorStatuses = () => vendorService.getVendorStatuses();
const getVendorAgreementTypes = () => vendorService.getVendorAgreementTypes();
const getVendorW9Statuses = () => vendorService.getVendorW9Statuses();
const getServiceWorkOrderCallReasons = () => swoService.getCallReasons();
const getSWODispatchFilterByStatus = () => swoService.getSWODispatchFilterByStatus();
const getSWODispatchFilterByTrade = () => swoService.getSWODispatchFilterByTrade();
const getInvoicePaymentFilters = () => invoiceService.getInvoicePaymentFilters();
const getSwoPurchaseRequestTypes = () => swoPurchasingService.getPurchaseRequestTypes();
const getCVIs = () => vendorService.getCVIs();
const getServiceWorkOrderBrands = (cviId: number) => {
  if (cviId >= 0) {
    return cviService.getBrands(cviId);
  } else {
    return Promise.resolve([] as OptionsViewModel[]);
  }
};
const getServiceWorkOrderStatuses = () => swoService.getStatuses();
const getServiceWorkOrderTypes = () => swoService.getTypes();
const getServiceWorkOrderRedispatchTypes = () => swoService.getRedispatchTypes();
const getServiceRequestTypes = () => authorizationService.getPurchaseRequestTypes();
const getBillingOptions = () => contractService.getBillingOptions();
const getNoRenewReasons = () => contractService.getNoRenewReasons();
const getSuppliers = () => swoPurchasingService.getSuppliers(false);
const getSuppliersHvac = () => swoPurchasingService.getSuppliers(true);
const getFacilities = (supplierId: number, stateAbbreviation?: string) => {
  if (supplierId > 0) {
    return swoPurchasingService.getFacilities(supplierId, stateAbbreviation);
  } else {
    return Promise.resolve([] as OptionsViewModel[]);
  }
};
const getColors = () => swoPurchasingService.getColors();
const getGLCodes = (payeeTypeCode?: number) => invoiceService.getGLCodes(payeeTypeCode);
const getSwoLineTypes = () => invoiceService.getLineTypes();
const getCILReasons = () => swoPurchasingService.getCILReasons();
const getFuel = () => swoPurchasingService.getFuel();
const getHvacSystemTypes = (supplierId: number) => {
  if (supplierId >= 0) {
    return swoPurchasingService.getHvacSystemTypes(supplierId);
  } else {
    return Promise.resolve([] as OptionsViewModel[]);
  }
};
const getHvacParts = (systemTypeId: number) => {
  if (systemTypeId >= 0) {
    return swoPurchasingService.getHvacParts(systemTypeId);
  } else {
    return Promise.resolve([] as OptionsViewModel[]);
  }
};
const getHvacVendorLocations = (supplierId: number) => {
  if (supplierId >= 0) {
    return swoPurchasingService.getHvacVendorLocations(supplierId);
  } else {
    return Promise.resolve([] as OptionsViewModel[]);
  }
};
const getInvoiceStatuses = () => invoiceService.getStatuses();

export const DynamicSelectGetters: { [property: string]: (id?: number, stateAbbreviation?: string) => Promise<{}> } = {
  [DynamicSelectIdentifier.Companies]: getCompanies,
  [DynamicSelectIdentifier.CustomerTypes]: getCustomerTypes,
  [DynamicSelectIdentifier.PhoneTypes]: getPhoneTypes,
  [DynamicSelectIdentifier.ContractCommentTypes]: getContractCommentTypes,
  [DynamicSelectIdentifier.CustomerStatuses]: getCustomerStatuses,
  [DynamicSelectIdentifier.CustomerLanguages]: getCustomerLanguages,
  [DynamicSelectIdentifier.DocumentStatuses]: getDocumentStatuses,
  [DynamicSelectIdentifier.RealEstateAgentTypes]: getRealEstateAgentTypes,
  [DynamicSelectIdentifier.RealEstateAgentRatings]: getRealEstateAgentRatings,
  [DynamicSelectIdentifier.RealEstateAgentStatuses]: getRealEstateAgentStatuses,
  [DynamicSelectIdentifier.EscrowOfficeGroups]: getEscrowOfficeGroups,
  [DynamicSelectIdentifier.EscrowOfficeStatuses]: getEscrowOfficeStatuses,
  [DynamicSelectIdentifier.EscrowOfficerTypes]: getEscrowOfficerTypes,
  [DynamicSelectIdentifier.EscrowOfficerStatuses]: getEscrowOfficerStatuses,
  [DynamicSelectIdentifier.RealEstateOfficeGroups]: getRealEstateOfficeGroups,
  [DynamicSelectIdentifier.RealEstateOfficeStatuses]: getRealEstateOfficeStatuses,
  [DynamicSelectIdentifier.RealEstateOfficeEOCarriers]: getRealEstateOfficeEOCarriers,
  [DynamicSelectIdentifier.TransactionCoordinatorTypes]: getTransactionCoordinatorTypes,
  [DynamicSelectIdentifier.TransactionCoordinatorStatuses]: getTransactionCoordinatorStatuses,
  [DynamicSelectIdentifier.USStates]: getUSStates,
  [DynamicSelectIdentifier.USFacilityStates]: getUSFacilityStates,
  [DynamicSelectIdentifier.PropertyManamentOfficeStatuses]: getPropertyManagmentOfficeStatuses,
  [DynamicSelectIdentifier.PropertyManagerStatuses]: getPropertyManagerStatuses,
  [DynamicSelectIdentifier.PropertyManagerTypes]: getPropertyManagerTypes,
  [DynamicSelectIdentifier.EdocTypes]: getEdocTypes,
  [DynamicSelectIdentifier.VendorDocumentTypes]: getVendorDocumentTypes,
  [DynamicSelectIdentifier.VendorAgreementTypes]: getVendorAgreementTypes,
  [DynamicSelectIdentifier.VendorGroups]: getVendorGroups,
  [DynamicSelectIdentifier.VendorStatuses]: getVendorStatuses,
  [DynamicSelectIdentifier.VendorTypes]: getVendorTypes,
  [DynamicSelectIdentifier.VendorW9Statuses]: getVendorW9Statuses,
  [DynamicSelectIdentifier.ServiceWorkOrderCallReasons]: getServiceWorkOrderCallReasons,
  [DynamicSelectIdentifier.ServiceWorkOrderBrands]: getServiceWorkOrderBrands,
  [DynamicSelectIdentifier.ServiceWorkOrderStatuses]: getServiceWorkOrderStatuses,
  [DynamicSelectIdentifier.ServiceWorkOrderTypes]: getServiceWorkOrderTypes,
  [DynamicSelectIdentifier.SWODispatchFilterByStatus]: getSWODispatchFilterByStatus,
  [DynamicSelectIdentifier.SWODispatchFilterByTrade]: getSWODispatchFilterByTrade,
  [DynamicSelectIdentifier.ServiceWorkOrderRedispatchTypes]: getServiceWorkOrderRedispatchTypes,
  [DynamicSelectIdentifier.ServiceRequestTypes]: getServiceRequestTypes,
  [DynamicSelectIdentifier.BillingOptions]: getBillingOptions,
  [DynamicSelectIdentifier.InvoicePaymentFilters]: getInvoicePaymentFilters,
  [DynamicSelectIdentifier.SWODispatchFilterByStatus]: getSWODispatchFilterByStatus,
  [DynamicSelectIdentifier.GetNoRenewReasons]: getNoRenewReasons,
  [DynamicSelectIdentifier.Suppliers]: getSuppliers,
  [DynamicSelectIdentifier.SuppliersHvac]: getSuppliersHvac,
  [DynamicSelectIdentifier.Facilities]: getFacilities,
  [DynamicSelectIdentifier.Colors]: getColors,
  [DynamicSelectIdentifier.GLCodes]: getGLCodes,
  [DynamicSelectIdentifier.SwoLineTypes]: getSwoLineTypes,
  [DynamicSelectIdentifier.CILReasons]: getCILReasons,
  [DynamicSelectIdentifier.SWOPurchaseRequestTypes]: getSwoPurchaseRequestTypes,
  [DynamicSelectIdentifier.CVIs]: getCVIs,
  [DynamicSelectIdentifier.Fuel]: getFuel,
  [DynamicSelectIdentifier.HvacSystemTypes]: getHvacSystemTypes,
  [DynamicSelectIdentifier.HvacParts]: getHvacParts,
  [DynamicSelectIdentifier.HvacVendorLocations]: getHvacVendorLocations,
  [DynamicSelectIdentifier.InvoiceStatuses]: getInvoiceStatuses
};

export interface DynamicSelectAction extends Action {
  readonly type: DynamicSelectActionType;
  readonly identifier: DynamicSelectIdentifier;
}

export interface DynamicSelectInitAction extends DynamicSelectAction {}

export interface DynamicSelectSuccessAction extends DynamicSelectAction {
  readonly options: OptionsViewModel[];
}

export interface DynamicSelectFailureAction extends DynamicSelectAction {
  readonly errorMessage: string;
}

export interface DynamicSelectClearOptionsAction extends DynamicSelectAction {}

// Actions for each action type declared here
export function loadOptionsInit(id: DynamicSelectIdentifier): DynamicSelectInitAction {
  return {
    type: DynamicSelectActionType.LoadOptionsInit,
    identifier: id
  };
}

export function loadOptionsSuccess(
  id: DynamicSelectIdentifier,
  options: OptionsViewModel[]
): DynamicSelectSuccessAction {
  return {
    type: DynamicSelectActionType.LoadOptionsSuccess,
    identifier: id,
    options: options
  };
}

export function loadOptionsFail(id: DynamicSelectIdentifier, err: string): DynamicSelectFailureAction {
  return {
    type: DynamicSelectActionType.LoadOptionsFail,
    identifier: id,
    errorMessage: err
  };
}

export function dynamicSelectLoadOptions(
  identifier: DynamicSelectIdentifier,
  searchId?: number,
  stateAbbreviation?: string
) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    const getOptions = DynamicSelectGetters[identifier];
    if (!getOptions) {
      return;
    }
    const store = getStore();
    const opts = store.dynamicSelect.options[identifier];
    if (
      opts &&
      opts.length > 0 &&
      identifier !== DynamicSelectIdentifier.Facilities &&
      identifier !== DynamicSelectIdentifier.HvacSystemTypes &&
      identifier !== DynamicSelectIdentifier.HvacParts &&
      identifier !== DynamicSelectIdentifier.HvacVendorLocations
    ) {
      return; // there are alredy options, so don't get them again
    }
    const isLoadingAlready = store.dynamicSelect.loadingOptions.indexOf(identifier) !== -1;
    if (isLoadingAlready) {
      return; // we're already loading the options, so don't bother, just wait
    }

    return getOptions(searchId ? searchId : 0, stateAbbreviation)
      .then((options: OptionsViewModel[]) => {
        return dispatch(loadOptionsSuccess(identifier, options));
      })
      .catch((err: string) => {
        return dispatch(loadOptionsFail(identifier, err));
      });
  };
}

export function clearOptions() {
  return {
    type: DynamicSelectActionType.ClearOptions
  };
}



// WEBPACK FOOTER //
// ./src/common/components/dynamic-select/dynamic-select.actions.tsx